import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import httpRequest from "src/helpers/httpRequest";
import { convertToKebabCase, parseQueryParams } from "src/helpers/helperFunctions";
import { QueryPath } from "src/lib/constans";
import {
  ChildCategoryType,
  ListType,
  SubCategoryType,
} from "src/types/GlobalInterfaces";
import CustomRadio from "../GlobalComponents/CustomRadio";
import { Loader } from "lucide-react";

interface IProps {
  title: string;
  List: ListType[];
  borderBottom?: boolean;
  childCategory?: string;
}
const SideCategoryList = ({ title, List, borderBottom = true }: IProps) => {
  const [mainCategoryCollapsed, setMainCategoryCollapsed] = useState<string[]>(
    []
  );
  const [subCategoryCollapsed, setSubCategoryCollapsed] = useState<string[]>(
    []
  );
  const [selectedChildCategory, setSelectedChildCategory] =
    useState<string>("");

  const [allCategories, setAllCategories] = useState<ListType[]>([]);
  const [loader, setLoader] = useState<boolean>(false);

  const getAllCategories = async () => {
    if (allCategories.length === 0) {
      setLoader(true);
      const { res, err } = await httpRequest<{
        mainCategory: any[];
        subCategory: any[];
        childCategory: any[];
      }>({
        path: "/main-category/home/all-categories",
      });
      if (res) {
        let mainCategory = res.mainCategory;
        let subCategory = res.subCategory;
        let childCategory = res.childCategory;
        let allCategories: ListType[] = mainCategory.map((item: any) => ({
          label: item.categoryName,
          value: item._id,
          id: item._id,
          pharmaInventoryCount: item.pharmaInventoryCount,
          subCategories: subCategory
            .filter((subItem: any) => subItem.mainCategoryId === item._id)
            .map((subItem: any) => ({
              label: subItem.subCategoryName,
              value: subItem._id,
              id: subItem._id,
              childCategories: childCategory
                .filter(
                  (childItem: any) => childItem.subCategoryId === subItem._id
                )
                .map((childItem: any) => ({
                  label: childItem.childCategoryName,
                  value: childItem._id,
                  id: childItem._id,
                })),
            })),
        }));

        setAllCategories(allCategories);
      } else {
        toast(err, { type: "error" });
      }
    }
    setLoader(false);
  };

  const [searchParams] = useSearchParams();
  const mainCategoryId = searchParams.get(QueryPath.mainCategory) || "";
  const subCategoryId = searchParams.get(QueryPath.subCategory) || "";
  const childCategoryId = searchParams.get(QueryPath.childCategory) || "";

  useEffect(() => {
    getAllCategories();
  }, []);

  useEffect(() => {
    if (mainCategoryId && subCategoryId) {
      setMainCategoryCollapsed([mainCategoryId]);
    }
  }, [mainCategoryId]);

  useEffect(() => {
    if (subCategoryId && childCategoryId) {
      setSubCategoryCollapsed([subCategoryId]);
    }
  }, [subCategoryId]);

  useEffect(() => {
    if (childCategoryId) {
      setSelectedChildCategory(childCategoryId);
    }
  }, [childCategoryId]);

  const navigate = useNavigate();
  return (
    <div
      className={`w-full p-2 2xl:p-3 ${
        borderBottom ? "border-b border-gray-300" : "border-none"
      }`}
    >
      <h2 className="font-Poppins-Medium font-size-18px theme-color-green">
        {title}
      </h2>
      <div className="w-full flex flex-col gap-2 mt-2">
        {loader && (
          <div className="w-full flex items-center justify-center h-[100px] gap-2">
            <Loader className="w-6 h-6 animate-spin" />
            <p className="font-Poppins-Regular font-size-15px text-[#2D2D2D]">
              Loading Categories
            </p>
          </div>
        )}
        {!loader &&
          allCategories.map((item: ListType, mainCategoryIndex) => (
            <div className="w-full flex flex-col gap-1">
              <div className="w-full flex gap-2 items-center">
                <div
                  id={`main-category-plus-${mainCategoryIndex + 1}`}
                  className="w-[17px] h-[17px] cursor-pointer rounded border border-gray-300 flex items-center justify-center"
                  onClick={() => {
                    if (mainCategoryCollapsed.includes(item?.id)) {
                      setMainCategoryCollapsed(
                        mainCategoryCollapsed.filter((id) => id !== item?.id)
                      );
                    } else {
                      setMainCategoryCollapsed([
                        ...mainCategoryCollapsed,
                        item?.id,
                      ]);
                    }
                  }}
                >
                  <FontAwesomeIcon
                    icon={
                      mainCategoryCollapsed.includes(item?.id)
                        ? faMinus
                        : faPlus
                    }
                    className="text-gray-500 text-[10px]"
                  />
                </div>
                <button
                  className="font-Poppins-Regular hover:font-medium hover:underline cursor-pointer font-size-15px text-[#2D2D2D]"
                  id={`main-category-${mainCategoryIndex + 1}`}
                  onClick={() => {
                    navigate(
                      `/category/${convertToKebabCase(
                        item?.label
                      )}?${parseQueryParams({
                        [QueryPath.mainCategory]: item?.id,
                        [QueryPath.mainCategoryName]: item?.label,
                      })}`
                    );
                    setSelectedChildCategory("");
                  }}
                >
                  {item.label} ({item?.pharmaInventoryCount})
                </button>
              </div>
              <div
                className={`w-full flex-col gap-2 border-l border-gray-300 ml-2 pl-4 overflow-hidden transition-all duration-300 ${
                  mainCategoryCollapsed.includes(item?.id) ? "flex" : "hidden"
                }`}
              >
                {item?.subCategories?.map(
                  (subItem: SubCategoryType, subCategoryIndex) => (
                    <div className="w-full flex flex-col gap-2">
                      <div className="w-full flex gap-2 items-center">
                        <div
                          id={`sub-category-plus-${subCategoryIndex + 1}`}
                          className="w-[17px] h-[17px] cursor-pointer rounded border border-gray-300 flex items-center justify-center"
                          onClick={() => {
                            if (subCategoryCollapsed.includes(subItem?.id)) {
                              setSubCategoryCollapsed(
                                subCategoryCollapsed.filter(
                                  (id) => id !== subItem?.id
                                )
                              );
                            } else {
                              setSubCategoryCollapsed([
                                ...subCategoryCollapsed,
                                subItem?.id,
                              ]);
                            }
                          }}
                        >
                          <FontAwesomeIcon
                            icon={
                              subCategoryCollapsed.includes(subItem?.id)
                                ? faMinus
                                : faPlus
                            }
                            className="text-gray-500 text-[10px]"
                          />
                        </div>
                        <button
                          id={`main-${mainCategoryIndex + 1}-sub-category-${
                            subCategoryIndex + 1
                          }`}
                          className="font-Poppins-Regular hover:font-medium hover:underline cursor-pointer font-size-15px text-[#2D2D2D]"
                          onClick={() => {
                            navigate(
                              `/category/${convertToKebabCase(
                                item?.label
                              )}/${convertToKebabCase(
                                subItem?.label
                              )}?${parseQueryParams({
                                [QueryPath.mainCategory]: item?.id,
                                [QueryPath.mainCategoryName]: item?.label,
                                [QueryPath.subCategory]: subItem?.id,
                                [QueryPath.subCategoryName]: subItem?.label,
                              })}`
                            );
                            setSelectedChildCategory("");
                          }}
                        >
                          {subItem.label}
                        </button>
                      </div>
                      <div
                        className={`w-full flex-col gap-2 border-l border-gray-300 ml-2 pl-4 overflow-hidden transition-all duration-300 ${
                          subCategoryCollapsed.includes(subItem?.id)
                            ? "flex"
                            : "hidden"
                        }`}
                      >
                        {subItem?.childCategories?.map(
                          (childItem: ChildCategoryType, childCategoryIdex) => (
                            <div className="w-full flex gap-2 items-center">
                              <CustomRadio
                                checked={
                                  selectedChildCategory === childItem?.id
                                }
                                onChange={() => {
                                  setSelectedChildCategory(childItem?.id);
                                  navigate(
                                    `/category/${convertToKebabCase(
                                      item?.label
                                    )}/${convertToKebabCase(
                                      subItem?.label
                                    )}/${convertToKebabCase(
                                      childItem?.label
                                    )}?${parseQueryParams({
                                      [QueryPath.mainCategory]: item?.id,
                                      [QueryPath.mainCategoryName]: item?.label,
                                      [QueryPath.subCategory]: subItem?.id,
                                      [QueryPath.subCategoryName]:
                                        subItem?.label,
                                      [QueryPath.childCategory]: childItem?.id,
                                      [QueryPath.childCategoryName]:
                                        childItem?.label,
                                    })}`
                                  );
                                }}
                                id={`child-cat-radio-${childCategoryIdex + 1}`}
                                className="!w-[17px] !h-[17px]"
                              />
                              <button
                                id={`main-${mainCategoryIndex + 1}-sub-${
                                  subCategoryIndex + 1
                                }-child-category-${childCategoryIdex + 1}`}
                                className="font-Poppins-Regular text-left line-clamp-2 font-size-15px hover:font-medium hover:underline cursor-pointer theme-color-green-2d"
                                onClick={() => {
                                  setSelectedChildCategory(childItem?.id);
                                  navigate(
                                    `/category/${convertToKebabCase(
                                      item?.label
                                    )}/${convertToKebabCase(
                                      subItem?.label
                                    )}/${convertToKebabCase(
                                      childItem?.label
                                    )}?${parseQueryParams({
                                      [QueryPath.mainCategory]: item?.id,
                                      [QueryPath.mainCategoryName]: item?.label,
                                      [QueryPath.subCategory]: subItem?.id,
                                      [QueryPath.subCategoryName]:
                                        subItem?.label,
                                      [QueryPath.childCategory]: childItem?.id,
                                      [QueryPath.childCategoryName]:
                                        childItem?.label,
                                    })}`
                                  );
                                }}
                              >
                                {childItem.label}
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default SideCategoryList;
